import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';

import { useArticleFetcher } from './hooks/useArticleStatsFetcher';
import { Hyperlink } from '../Hyperlink';

import styles from './article_stats.module.scss';

type Props = {
  articleId: string;
  url: string;
  sourcePage: string;
  queryParam?: string;
};

const getFullSourceFromPageAndType = (page: string, type: string) => {
  if (Object.is(page, 'decisionPageLink_popover')) {
    const adjustedType = Object.is(type, 'parliamentary-questions')
      ? 'parliamentary_questions'
      : type;
    return [page, adjustedType].join('_');
  }
  return [page, type].join('-');
};

const makeUrl = ({
  url,
  source,
  hash,
  filterParam,
  queryParam,
}: {
  url: string;
  source: string;
  hash: string;
  filterParam?: string;
  queryParam?: string;
}) => {
  const setFilterParam = filterParam?.length ? `&filter=${filterParam}` : '';
  const setQueryParam = queryParam?.length ? `&${queryParam}` : '';
  if (url.includes('?'))
    return `${url}${setQueryParam}${setFilterParam}&source=${source}#${hash}`;
  return `${url}?source=${source}${setQueryParam}${setFilterParam}#${hash}`;
};

export const ArticleStats: FC<Props> = ({
  articleId,
  url,
  sourcePage,
  queryParam = '',
}) => {
  const { loading, stats } = useArticleFetcher({ articleId });

  if (loading) return <div className={styles.statsLoader} />;
  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <ol className={styles.list}>
          {stats.commentaries > 0 && (
            <li className={styles.item}>
              <Hyperlink
                href={makeUrl({
                  url,
                  source: getFullSourceFromPageAndType(
                    sourcePage,
                    'commentaries',
                  ),
                  hash: 'commentaries',
                  queryParam,
                })}
                className={styles.itemLink}
              >
                {stats.commentaries > 500 ? '+500' : stats.commentaries}{' '}
                <FormattedMessage id="article-stats/ArticleStats.tsx/3" />
              </Hyperlink>
            </li>
          )}
          {stats.referenceDecisions > 0 && (
            <li className={styles.item}>
              <Hyperlink
                href={makeUrl({
                  url,
                  source: getFullSourceFromPageAndType(sourcePage, 'decisions'),
                  hash: 'decisions',
                  filterParam: 'reference',
                  queryParam,
                })}
                className={styles.itemLink}
              >
                {stats.referenceDecisions > 100
                  ? '+100'
                  : stats.referenceDecisions}{' '}
                <FormattedMessage id="article-stats/ArticleStats.tsx/2" />
              </Hyperlink>
            </li>
          )}
          {stats.qpc > 0 && (
            <li className={styles.item}>
              <Hyperlink
                href={makeUrl({
                  url,
                  source: getFullSourceFromPageAndType(sourcePage, 'qpc'),
                  hash: 'decisions',
                  filterParam: 'qpc',
                  queryParam,
                })}
                className={styles.itemLink}
              >
                {stats.qpc > 100 ? '+100' : stats.qpc}{' '}
                <FormattedMessage id="article-stats/ArticleStats.tsx/1" />
              </Hyperlink>
            </li>
          )}
          {stats.parliamentaryQuestions > 0 && (
            <li className={styles.item}>
              <Hyperlink
                href={makeUrl({
                  url,
                  source: getFullSourceFromPageAndType(
                    sourcePage,
                    'parliamentary-questions',
                  ),
                  hash: 'commentaries',
                  filterParam: 'parliamentary_questions',
                  queryParam,
                })}
                className={styles.itemLink}
              >
                {stats.parliamentaryQuestions > 100
                  ? '+100'
                  : stats.parliamentaryQuestions}{' '}
                <FormattedMessage id="article-stats/ArticleStats.tsx/4" />
              </Hyperlink>
            </li>
          )}
        </ol>
      </div>
    </div>
  );
};
